import { useInfiniteQuery } from 'react-query'

import { api, handleCommonErrors, useMutationApi, useQueryApi } from 'utils/api'
import { CustomError } from 'utils/customError'

import type { UseMutationOptions } from 'react-query'
import type { AksiNyataSession } from 'types/microlearning'
import type { CommonListResponse, CommonResponse } from 'types/api'
import type { AksiNyataFeedback, PublicAksiNyata } from 'types/microlearning'

export class FeedbackUnauthorized extends CustomError {
  constructor(apiPath: string, originalError: any) {
    let title = 'Tidak Dapat Ditampilkan'
    let description =
      'Anda tidak memiliki akses untuk melihat Aksi Nyata milik guru lain.'
    super({ title, description, apiPath, originalError })
  }
}

export function useGetFeedbackAuthorizeStatus(isEnabled = true) {
  return useQueryApi(`/microlearnings/feedbacks/authorized`, {
    queryConfig: {
      enabled: isEnabled,
      useErrorBoundary: false,
    },
  })
}

interface AksiNyataItem extends AksiNyataSession {
  selectedFormat: string
  topicId: string
  topicName: string
  updatedAtUnix: number
}

export function useGetAksiNyataList(updatedAtUnix?: number, limit = 10) {
  return useInfiniteQuery<
    CommonListResponse<
      Pick<AksiNyataItem, 'id' | 'title' | 'creator' | 'topicId' | 'topicName'>
    >
  >(
    ['aksinyata-infinite-query'],
    async ({ pageParam = { updatedAtUnix, limit } }) =>
      api<CommonListResponse<AksiNyataItem>>(
        `/microlearnings/aksi-nyata-validations`,
        {
          params: pageParam,
        }
      ),
    {
      select: (data) => {
        return {
          pages: data.pages.map((page) => {
            return {
              data: page.data.map(
                ({ id, title, creator, topicId, topicName }) => ({
                  id,
                  title,
                  creator,
                  topicId,
                  topicName,
                })
              ),
              meta: {},
            }
          }),
          pageParams: data.pageParams,
        }
      },
      getNextPageParam: (lastPage: CommonListResponse<AksiNyataItem>) => {
        return lastPage.data.length > 0
          ? {
              updatedAtUnix:
                lastPage.data[lastPage.data.length - 1].updatedAtUnix,
              limit,
            }
          : undefined
      },
    }
  )
}

export function useGetPublicAksiNyata(
  aksiNyataId: number | string,
  enabled: boolean = true
) {
  return useQueryApi<CommonResponse<PublicAksiNyata>>(
    `/microlearnings/aksi-nyata/${aksiNyataId}`,
    {
      queryConfig: {
        enabled: !!aksiNyataId && enabled,
      },
      errorHandlerFn: (status, path, error) => {
        handleCommonErrors(status ?? 500, path, error)
      },
    }
  )
}

export function useGetAksiNyataFeedback(
  aksiNyataId: number | string,
  isEnabled: boolean = false
) {
  return useQueryApi<CommonListResponse<AksiNyataFeedback>>(
    `/microlearnings/aksi-nyata/${aksiNyataId}/feedbacks`,
    {
      queryConfig: {
        enabled: !!aksiNyataId && isEnabled,
        useErrorBoundary: false,
      },
      errorHandlerFn: (status, path, error) => {
        if (status === 401) {
          throw new FeedbackUnauthorized(path, error)
        } else {
          handleCommonErrors(status, path, error)
        }
      },
    }
  )
}

export function usePostAksiNyataFeedback(
  aksiNyataId: string | number,
  opts?: UseMutationOptions<unknown, Error, unknown>
) {
  return useMutationApi<unknown, Error, unknown>(
    `/microlearnings/aksi-nyata/${aksiNyataId}/feedbacks`,
    {
      mutationConfig: opts,
      axiosConfig: {
        method: 'POST',
      },
    }
  )
}
